import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { CaseFormRepository } from '../../../infrastructure/repositories/case-form.repository';
import { CaseFormState } from '../../states/case-form.state';
import { FieldConstraintTypeResponseDto } from '../../../infrastructure/dtos/field-constraint-type-response.dto';
import { CaseFormMapper } from '../../../infrastructure/mappers/case-form.mapper';

/**
 * Get Field Constraint Types Use Case
 * Retrieves all field constraint types and field types (with operators)
 *
 * Constraints are cached in state and only fetched once per session
 * since they don't change frequently
 */
@Injectable({ providedIn: 'root' })
export class GetFieldConstraintTypesUseCase {
  private readonly _repository = inject(CaseFormRepository);
  private readonly _state = inject(CaseFormState);

  public execute(): Observable<FieldConstraintTypeResponseDto[]> {
    // If constraints are already in state, return them from state
    const cachedConstraints = this._state.fieldConstraintTypes();
    if (cachedConstraints.length > 0) {
      return of(cachedConstraints);
    }

    // Otherwise, fetch from API and cache in state
    // Fetch field types once and derive both operators and flattened constraints
    this._state.setLoadingFieldConstraintTypes(true);

    return this._repository.getFieldTypes().pipe(
      map((fieldTypes) => {
        this._state.setFieldTypes(fieldTypes);
        const constraints = CaseFormMapper.flattenFieldTypeConstraints(fieldTypes);
        this._state.setFieldConstraintTypes(constraints);
        return constraints
      }),
      finalize(() => this._state.setLoadingFieldConstraintTypes(false))
    );
  }
}
